<template>
  <div class="admin-dashboard">
    <el-container>
      <el-aside :width="sidebarWidth" class="sidebar" :class="{ collapsed: isCollapse }">
        <div class="logo" @click="goToDashboard">
          <div class="logo-wrapper">
            <el-icon class="logo-icon"><Management /></el-icon>
            <transition name="fade">
              <h3 v-show="!isCollapse" class="logo-text">管理后台</h3>
            </transition>
          </div>
        </div>
        
        <el-menu 
          :default-active="activeMenu" 
          class="sidebar-menu" 
          :collapse="isCollapse"
          :collapse-transition="false"
          @select="handleMenuSelect"
        >
          <template v-for="menu in visibleUserMenus" :key="menu.id">
            <el-sub-menu v-if="menu.children && menu.children.length > 0" :index="String(menu.id)">
              <template #title>
                <el-icon v-if="isValidIconName(menu.icon)">
                  <component :is="menu.icon.toLowerCase()" />
                </el-icon>
                <span v-show="!isCollapse">{{ menu.name }}</span>
              </template>
              
              <template v-for="child in menu.children" :key="child.id">
                <el-menu-item 
                  v-if="child.visible !== 0" 
                  :index="generateMenuPath(child)"
                  @click="navigateToPage(child)"
                >
                  <el-icon v-if="isValidIconName(child.icon)">
                    <component :is="child.icon.toLowerCase()" />
                  </el-icon>
                  <span>{{ child.name }}</span>
                </el-menu-item>
              </template>
            </el-sub-menu>
            
            <el-menu-item 
              v-else-if="menu.visible !== 0" 
              :index="String(menu.id)"
              @click="navigateToPage(menu)"
            >
              <el-icon v-if="isValidIconName(menu.icon)">
                <component :is="menu.icon.toLowerCase()" />
              </el-icon>
              <span>{{ menu.name }}</span>
            </el-menu-item>
          </template>
        </el-menu>
        
        <div class="toggle-collapse">
          <el-button 
            @click="toggleCollapse"
            :icon="isCollapse ? 'Expand' : 'Fold'"
            size="default"
            circle
          />
        </div>
      </el-aside>
      
      <el-container>
        <el-header class="dashboard-header">
          <div class="header-content">
            <h2 class="page-title">{{ pageTitle }}</h2>
            <div class="user-actions">
              <el-dropdown placement="bottom-end">
                <div class="user-profile">
                  <el-avatar :size="40" :src="userInfo?.avatar || defaultAvatar" />
                  <div class="user-info" v-show="!isCollapse">
                    <span class="username">{{ userInfo?.username }}</span>
                    <br />
                    <span class="role">{{ userInfo?.roleName }}</span>
                  </div>
                </div>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item @click="goToProfile">
                      <el-icon><User /></el-icon>
                      个人资料
                    </el-dropdown-item>
                    <el-dropdown-item @click="logout" divided>
                      <el-icon><SwitchButton /></el-icon>
                      退出登录
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </div>
        </el-header>
        
        <el-main class="main-content">
          <!-- 动态内容区域，根据选中的菜单显示 -->
          <div v-if="currentComponent" class="dynamic-content">
            <component :is="currentComponent" />
          </div>
          
          <WelcomeSection v-else :userInfo="userInfo" />
        </el-main>
      </el-container>
    </el-container>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, markRaw } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { 
  HomeFilled, 
  Setting, 
  User, 
  UserFilled, 
  Lock, 
  Menu, 
  Document, 
  ChatDotSquare, 
  Checked, 
  DocumentCheck, 
  ChatCheck, 
  Warning,
  Management,
  ChatLineRound,
  SwitchButton
} from '@element-plus/icons-vue';
import { getCurrentUserInfo } from '@/admin/api/user'; // 导入获取当前用户信息的API
import { logout as adminLogout } from '@/admin/api/user'; // 导入退出登录API
import WelcomeSection from '@/admin/views/dashboard/DashboardView.vue';

const router = useRouter();

// 响应式数据
const userMenus = ref<any[]>([]);
const userInfo = ref<any>(null);
const activeMenu = ref('');
const currentComponent = ref(null);
const pageTitle = ref('管理后台');
const isCollapse = ref(false);
const defaultAvatar = ref('https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png');

// 计算属性：只显示可见的菜单
const visibleUserMenus = computed(() => {
  return userMenus.value.filter(menu => {
    // 如果菜单本身可见，直接返回
    if (menu.visible !== 0) return true;
    
    // 如果菜单本身不可见，但是有子菜单且至少有一个子菜单可见，则显示父菜单（但不会展开）
    if (menu.children && menu.children.length > 0) {
      return menu.children.some((child: any) => child.visible !== 0);
    }
    
    return false;
  });
});

// 验证图标名称是否有效
const isValidIconName = (iconName?: string) => {
  if (!iconName) return false;
  
  // 检查是否只包含字母数字和连字符/下划线
  const validNamePattern = /^[a-zA-Z][a-zA-Z0-9_-]*$/;
  
  // 检查名称是否符合Element Plus图标组件命名规范
  // 图标名称应该是合理的组件名，不能是纯数字或其他无效字符
  return validNamePattern.test(iconName) && isNaN(Number(iconName));
};

// 菜单路径到组件的映射
const menuComponentMap: Record<string, any> = {
  '/dashboard': markRaw(() => import('@/admin/views/dashboard/DashboardView.vue')),
  '/system/user': markRaw(() => import('@/admin/views/system/user/UserManagementView.vue')),
  '/system/role': markRaw(() => import('@/admin/views/system/role/RoleManagementView.vue')),
  '/system/permission': markRaw(() => import('@/admin/views/system/permission/PermissionManagementView.vue')),
  '/system/menu': markRaw(() => import('@/admin/views/system/menu/MenuManagementView.vue')),
  '/content/post': markRaw(() => import('@/admin/views/content/post/PostManagementView.vue')),
  '/content/comment': markRaw(() => import('@/admin/views/content/comment/CommentManagementView.vue')),
  '/audit/post': markRaw(() => import('@/admin/views/audit/post/PostAuditView.vue')),
  '/audit/comment': markRaw(() => import('@/admin/views/audit/comment/CommentAuditView.vue')),
  '/report/comment': markRaw(() => import('@/admin/views/report/comment/CommentReportView.vue')),
  '/report/post': markRaw(() => import('@/admin/views/report/post/PostReportView.vue')),
  '/report/user': markRaw(() => import('@/admin/views/report/user/UserReportView.vue')),
  '/system/announcement': markRaw(() => import('@/admin/views/system/announcement/AnnouncementManagementView.vue')),
  '/system/monitor': markRaw(() => import('@/admin/views/system/monitor/SystemMonitorView.vue')),
  '/system/login-log': markRaw(() => import('@/admin/views/system/login-log/LoginLogManagementView.vue')),
};

// 计算属性
const sidebarWidth = computed(() => isCollapse.value ? '64px' : '220px');

// 加载用户菜单和信息
onMounted(() => {
  loadUserData();
});

const loadUserData = () => {
  try {
    const menusStr = localStorage.getItem('adminMenus');
    const userInfoStr = localStorage.getItem('adminUserInfo');
    
    if (menusStr) {
      userMenus.value = JSON.parse(menusStr);
    } else {
      ElMessage.error('未能获取用户菜单权限');
    }
    
    if (userInfoStr) {
      userInfo.value = JSON.parse(userInfoStr);
      // 即使本地有用户信息，也异步更新为最新信息
      setTimeout(() => {
        fetchCurrentUser();
      }, 0);
    } else {
      // 如果本地没有用户信息，尝试从后端获取
      fetchCurrentUser();
    }
  } catch (error) {
    console.error('加载用户数据失败:', error);
    ElMessage.error('加载用户数据失败');
  }
};

// 获取当前登录用户信息
const fetchCurrentUser = async () => {
  try {
    // 这里需要调用获取当前用户信息的API
    // 假设后端提供了获取当前管理员信息的接口
    const response = await getCurrentUserInfo();
    if (response.code === 1) {
      userInfo.value = response.data;
      // 同时保存到本地存储
      localStorage.setItem('adminUserInfo', JSON.stringify(response.data));
      console.log('成功获取当前用户信息:', response.data);
    } else {
      console.error('获取用户信息失败:', response.msg);
      ElMessage.error(response.msg || '获取用户信息失败');
    }
  } catch (error) {
    console.error('获取当前用户信息失败:', error);
    ElMessage.error('获取当前用户信息失败，请检查网络连接');
  }
};

// 生成菜单路径
const generateMenuPath = (menu: any) => {
  // 如果路径是以/开头，直接返回
  if (menu.path.startsWith('/')) {
    return menu.path;
  }
  // 否则加上/前缀
  return '/' + menu.path;
};

// 处理菜单选择
const handleMenuSelect = (index: string) => {
  // 查找对应的菜单项
  const findMenuById = (menuItems: any[], id: string) => {
    for (const item of menuItems) {
      if (String(item.id) === id) {
        return item;
      }
      
      if (item.children && item.children.length > 0) {
        const found = findMenuById(item.children, id);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  const selectedMenu = findMenuById(visibleUserMenus.value, index);
  
  // 如果是目录类型的菜单（有子菜单），则不进行页面导航，只更新激活状态
  if (selectedMenu && selectedMenu.children && selectedMenu.children.length > 0) {
    activeMenu.value = index;
    // 目录菜单不应该导航到页面，而是展开子菜单
    return;
  }
  
  // 如果是叶子节点菜单，则导航到对应页面
  if (selectedMenu) {
    activeMenu.value = index;
    navigateToPage(selectedMenu);
  }
};

// 导航到指定页面
const navigateToPage = async (menu: any) => {
  // 更新标题
  pageTitle.value = menu.name;
  
  // 构建路径
  let path = menu.path;
  if (!path.startsWith('/')) {
    path = '/' + path;
  }
  
  // 检查路径是否为目录类型（没有直接对应的组件），如果是，则寻找第一个可用的子菜单
  if (!menuComponentMap[path]) {
    // 从原始菜单数据中查找子菜单，因为visibleUserMenus是树形结构
    const findSubmenu = (menuItems, parentId) => {
      for (const item of menuItems) {
        if (item.parentId === parentId && item.visible !== 0) {
          return item;
        }
        
        // 如果当前项有子项，递归查找
        if (item.children && item.children.length > 0) {
          const subItem = findSubmenu(item.children, parentId);
          if (subItem) {
            return subItem;
          }
        }
      }
      return null;
    };
    
    // 获取完整的菜单数据（这里假设有一个包含所有菜单的变量）
    // 我们可以使用visibleUserMenus作为源数据
    const submenu = findSubmenu(visibleUserMenus.value, menu.id);
    
    if (submenu) {
      // 如果有子菜单，跳转到子菜单
      console.log(`目录菜单 ${menu.name}，跳转到子菜单: ${submenu.name}`);
      navigateToPage(submenu);
      return;
    }
    
    // 如果路径不在映射中，显示提示
    console.warn(`未找到路径 "${path}" 对应的组件`);
    ElMessage.warning(`${menu.name} 页面正在开发中`);
    currentComponent.value = null;
    return;
  }
  
  // 根据路径尝试动态加载组件
  try {
    if (menuComponentMap[path]) {
      const module = await menuComponentMap[path]();
      currentComponent.value = markRaw(module.default);
    } else {
      // 如果路径不在映射中，显示提示
      console.warn(`未找到路径 "${path}" 对应的组件`);
      ElMessage.warning(`${menu.name} 页面正在开发中`);
      currentComponent.value = null;
    }
  } catch (err) {
    console.error(`加载组件失败: ${path}`, err);
    ElMessage.error(`${menu.name} 页面加载失败`);
    currentComponent.value = null;
  }
};

const logout = async () => {
  try {
    await ElMessageBox.confirm(
      '确定要退出管理后台吗？',
      '确认退出',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    );

    // 调用退出登录API
    const response = await adminLogout();
    if(response.code !== 1) {
      console.warn('服务器退出登录失败:', response.msg);
    }

    // 清除管理员token
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUserInfo');
    localStorage.removeItem('adminMenus');
    ElMessage.success('已退出登录');
    router.push('/admin/login');
  } catch (error) {
    console.log('取消退出');
  }
};

const toggleCollapse = () => {
  isCollapse.value = !isCollapse.value;
};

const goToProfile = () => {
  // 跳转到个人资料页面
  pageTitle.value = '个人资料';
  currentComponent.value = null; // 这里可以改为加载实际的个人资料组件
};

const goToDashboard = () => {
  router.push('/admin/dashboard');
  pageTitle.value = '管理后台';
  currentComponent.value = null;
};
</script>

<style scoped>
.admin-dashboard {
  height: 100vh;
  background-color: #f5f7fa;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  display: flex; /* 使用flexbox布局 */
  overflow: hidden; /* 防止整个页面出现不必要的滚动条 */
}

.sidebar {
  background-color: #222b45;
  height: 100vh;
  min-height: 0; /* 使侧边栏也可以收缩 */
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
  z-index: 100; /* 确保侧边栏在最前面 */
  flex-shrink: 0; /* 防止侧边栏被压缩 */
}

.sidebar.collapsed {
  overflow-x: hidden;
}

.logo {
  height: 60px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  cursor: pointer;
  border-bottom: 1px solid #323a55;
  transition: all 0.3s;
  flex-shrink: 0; /* 防止logo被压缩 */
}

.logo:hover {
  background-color: #1a2237;
}

.logo-wrapper {
  display: flex;
  align-items: center;
  width: 100%;
}

.logo-icon {
  font-size: 24px;
  color: #409eff;
  margin-right: 8px;
}

.logo-text {
  color: white;
  font-size: 18px;
  font-weight: 500;
  margin: 0;
  transition: opacity 0.3s;
}

.sidebar-menu {
  border: none;
  background-color: transparent;
  height: calc(100% - 120px); /* 调整菜单高度以适应底部按钮 */
  overflow-y: auto; /* 当菜单过多时允许滚动 */
}

.sidebar-menu :deep(.el-sub-menu__title) {
  background-color: #2c344a;
  color: #a2a9bb;
  height: 48px;
  line-height: 48px;
}

.sidebar-menu :deep(.el-menu-item) {
  background-color: #2c344a;
  color: #a2a9bb;
  height: 42px;
  line-height: 42px;
}

/* 设置菜单的基础缩进 */
.sidebar-menu :deep(.el-sub-menu .el-menu) {
  background-color: #2c344a;
}

/* 确保子菜单项有正确的缩进 */
.sidebar-menu :deep(.el-sub-menu .el-menu .el-menu-item) {
  background-color: #3a445d;
  padding-left: 45px !important;
}

/* 更深层级的菜单项缩进 */
.sidebar-menu :deep(.el-sub-menu .el-menu .el-sub-menu .el-menu .el-menu-item) {
  padding-left: 65px !important;
}

/* 确保子菜单项的缩进不会重置 */
.sidebar-menu :deep(.el-sub-menu .el-menu .el-sub-menu .el-menu-item) {
  padding-left: 65px !important;
}

/* 修正菜单项缩进问题 - 重点修复新建菜单项的缩进 */
.sidebar-menu :deep(.el-sub-menu .el-menu .el-menu-item),
.sidebar-menu :deep(.el-sub-menu .el-menu .el-sub-menu__title) {
  padding-left: 45px !important;
}

/* 为子菜单项添加更明确的缩进 */
.sidebar-menu :deep(.el-sub-menu .el-menu .el-sub-menu .el-menu .el-sub-menu__title) {
  padding-left: 65px !important;
}

.sidebar-menu :deep(.el-sub-menu):not(.is-active):hover .el-sub-menu__title,
.sidebar-menu :deep(.el-menu-item):hover {
  background-color: #3a445d;
  color: white;
}

.sidebar-menu :deep(.el-menu-item.is-active) {
  background-color: #409eff;
  color: white;
  border-right: 3px solid #409eff;
}

.toggle-collapse {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
}

.toggle-collapse .el-button {
  background-color: #409eff;
  border-color: #409eff;
  color: white;
  width: 32px;
  height: 32px;
}

.toggle-collapse .el-button:hover {
  background-color: #66b1ff;
  border-color: #66b1ff;
}

.dashboard-header {
  padding: 0;
  background-color: white;
  box-shadow: 0 1px 4px rgba(0,21,41,.08);
  height: 60px;
  display: flex;
  align-items: center;
  flex-shrink: 0; /* 防止头部被压缩 */
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 20px;
}

.page-title {
  margin: 0;
  color: #303133;
  font-weight: 500;
  font-size: 18px;
}

.user-actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

.user-profile {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  transition: background-color 0.3s;
}

.user-profile:hover {
  background-color: #f5f7fa;
}

.user-info {
  display: flex;
  flex-direction: column;
  margin-right: 8px;
}

.username {
  font-size: 14px;
  color: #303133;
  font-weight: 500;
}

.role {
  font-size: 12px;
  color: #909399;
}

.main-content {
  background-color: #f5f7fa;
  padding: 20px;
  flex: 1; /* 使用 flex: 1 使内容区域填充剩余空间 */
  min-height: 0; /* 允许内容区域收缩 */
  overflow: hidden; /* 隐藏滚动条，内容需适应容器 */
  display: flex;
  flex-direction: column;
}

.welcome-section {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 20px;
  flex: 1;
  overflow-y: auto; /* 只在必要时才滚动内容 */
}

.welcome-card {
  width: 100%;
  max-width: 1200px;
  border-radius: 12px;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.card-header {
  border-bottom: 1px solid #ebeef5;
  padding: 20px 24px;
}

.card-header h3 {
  margin: 0;
  color: #303133;
  font-size: 18px;
  font-weight: 500;
}

.welcome-content {
  padding: 24px;
  text-align: center;
}

.welcome-content p {
  font-size: 16px;
  color: #606266;
  margin: 10px 0;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-top: 30px;
}

.stat-card {
  border-radius: 12px;
  border: none;
  box-shadow: 0 2px 8px rgba(0,0,0,.08);
  transition: transform 0.3s, box-shadow 0.3s;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 16px rgba(0,0,0,.12);
}

.stat-item {
  display: flex;
  align-items: center;
  padding: 16px;
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  color: white;
  font-size: 24px;
}

.bg-blue {
  background: linear-gradient(135deg, #409eff, #337ecc);
}

.bg-green {
  background: linear-gradient(135deg, #67c23a, #529b2e);
}

.bg-orange {
  background: linear-gradient(135deg, #e6a23c, #c18b33);
}

.bg-purple {
  background: linear-gradient(135deg, #9013fe, #7410cb);
}

.stat-info {
  flex: 1;
  text-align: left;
}

.stat-number {
  font-size: 24px;
  font-weight: bold;
  margin: 0 0 5px 0;
  color: #303133;
  line-height: 1.2;
}

.stat-label {
  font-size: 14px;
  margin: 0;
  color: #909399;
  line-height: 1.4;
}

.dynamic-content {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,.05);
  flex: 1; /* 使用flex填充剩余空间 */
  overflow-y: auto; /* 如果内容超出则内部滚动 */
  min-height: 0; /* 允许内容区域收缩 */
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .header-content {
    padding: 0 10px;
  }
}
</style>